import { useState, useEffect } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client/react';
import { GET_FLOW_CONTACTS } from '../../../lib/graphql/queries/organizations';
import { Contact } from '../../../types/contacts';

export const PAGE_SIZE = 25;

interface GQLFlowContactSummary {
  contactId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  contactStatus: string | null;
  lifecycleStage: string | null;
  tags: string[] | null;
  executionId: string | null;
  executionStatus: string | null;
  currentStep: number | null;
  totalSteps: number | null;
  nextScheduledAt: string | null;
  flowCreatedAt: string | null;
}

interface GetFlowContactsResult {
  flowContacts: {
    items: GQLFlowContactSummary[];
    total: number;
    hasMore: boolean;
  };
}

function mapGQLContact(c: GQLFlowContactSummary, organizationId: string): Contact {
  return {
    id: c.contactId,
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    phoneNumber: c.phone ?? '',
    tags: c.tags ?? [],
    organization_id: organizationId,
    fullName: `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || '-',
    executionStatus: c.executionStatus ?? '',
    flowCreatedAt: c.flowCreatedAt ?? null,
  };
}

interface UseFlowContactsGQLOptions {
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const useFlowContactsGQL = (flowId: string, organizationId: string, searchOrOptions?: string | UseFlowContactsGQLOptions) => {
  const opts: UseFlowContactsGQLOptions = typeof searchOrOptions === 'string' ? { search: searchOrOptions } : searchOrOptions ?? {};
  const search = opts.search || undefined;
  const sortBy = opts.sortBy || 'flowCreatedAt';
  const sortOrder = (opts.sortOrder || 'desc').toUpperCase();

  const [currentPage, setCurrentPage] = useState(0);
  const offset = currentPage * PAGE_SIZE;

  // Reset page when flow, search, or sort changes
  useEffect(() => {
    setCurrentPage(0);
  }, [flowId, search, sortBy, sortOrder]);

  const { data, loading, error, refetch } = useApolloQuery<GetFlowContactsResult>(
    GET_FLOW_CONTACTS,
    {
      variables: { organizationId, flowId, limit: PAGE_SIZE, offset, sortBy, sortOrder, search },
      skip: !organizationId || !flowId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const contacts = (data?.flowContacts?.items ?? []).map((c) => mapGQLContact(c, organizationId));
  const total = data?.flowContacts?.total ?? 0;
  const hasMore = data?.flowContacts?.hasMore ?? false;

  return {
    contacts,
    total,
    isLoading: loading,
    hasMore,
    currentPage,
    setCurrentPage,
    refetch,
    error: error ?? null,
  };
};
